import { useMutation } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { memo, Suspense, useCallback, useEffect, useRef } from 'react'
import 'simplebar-react/dist/simplebar.min.css'

import { type ButtonClickContext, PharaohWrapper, ToastProvider } from '@orus.eu/pharaoh'
import { TemporaryNotificationAlertProvider } from '@orus.eu/pharaoh/src/components/callout-notification/notification/alert'
import { isSuccess } from '@orus.eu/result'

import { useTRPC } from './client.ts'
import { GlobalLoadingState } from './components/molecules/global-loading-state.tsx'
import { EmbeddingPartnerWrapper } from './components/templates/embedding-partner-wrapper.tsx'
import { OperatingZoneWrapper } from './components/templates/operating-zone-wrapper.tsx'
import { SessionDefaultLanguageWrapper } from './components/templates/session-default-language-wrapper.tsx'
import { SessionLanguageProvider } from './components/templates/session-language-provider.tsx'
import { SessionProvider } from './components/templates/session-provider.tsx'
import { logger } from './lib/logger.ts'
import { initializePostHogEventRenaming } from './lib/posthog-utils.ts'
import { sessionManager } from './lib/session.ts'
import { getPageNameAndResourceIdFromRoute } from './lib/tracking/page-identification.ts'
import { useStartTrackingClicksOnTrustpilotWidget } from './lib/tracking/start-tracking-clicks-on-trustpilot-widget.ts'
import { createTrackingMessage, sendMessage } from './lib/tracking/tracking.ts'
import { router } from './router.tsx'
import { UpdateHandler } from './update.tsx'

export const App = memo(function App() {
  const trpc = useTRPC()
  const { mutate: sendEvent } = useMutation(trpc.log.sendEvent.mutationOptions())
  const loggerTransportInitializedRef = useRef(false)

  useEffect(() => {
    if (loggerTransportInitializedRef.current) return
    loggerTransportInitializedRef.current = true

    void sessionManager.getSessionResult().then(() => {
      logger.addTransport({
        event(event, metadata) {
          sendEvent({ event, metadata })
        },
      })
    })
  }, [sendEvent])

  // Initialize PostHog event renaming for activity search events
  useEffect(() => {
    initializePostHogEventRenaming()
  }, [])

  // Track Trustpilot widget clicks
  useStartTrackingClicksOnTrustpilotWidget()

  const sendMessageCallback = useCallback((context: ButtonClickContext) => {
    const matches = router.matchRoutes(window.location.pathname, window.location.search)
    const match = matches.at(-1)
    if (!match) return
    const resourceAndPath = getPageNameAndResourceIdFromRoute({ routeId: match.routeId, params: match.params })

    // Special handling for contact advisor button
    if (context.buttonName === 'contact_button') {
      sendMessage(
        createTrackingMessage({
          event: 'contact_advisor_clicked',
        }),
      )
      return
    }

    // Special handling for WhatsApp button
    if (context.buttonIcon === 'whatsapp-brands') {
      sendMessage(
        createTrackingMessage({
          event: 'whatsapp_conversation_button_clicked',
        }),
      )
      return
    }

    // Special handling for phone button
    if (context.buttonName === 'call_button') {
      sendMessage(
        createTrackingMessage({
          event: 'phone_number_button_clicked',
          phone_number: context.buttonText ?? null,
        }),
      )
      return
    }

    sendMessage(
      createTrackingMessage({
        event: 'click_button',
        button_name: context.buttonName ?? null,
        button_icon: context.buttonIcon ?? null,
        button_text: context.buttonText ?? null,
        page_name: isSuccess(resourceAndPath) ? resourceAndPath.output.pageName : null,
        page_resource_id: isSuccess(resourceAndPath) ? resourceAndPath.output.resourceId : null,
      }),
    )
  }, [])

  return (
    <PharaohWrapper clickButtonCallback={sendMessageCallback}>
      <TemporaryNotificationAlertProvider>
        <ToastProvider>
          <SessionProvider>
            <SessionDefaultLanguageWrapper>
              <SessionLanguageProvider>
                <OperatingZoneWrapper>
                  <EmbeddingPartnerWrapper>
                    <Suspense fallback={<GlobalLoadingState />}>
                      {/* Catches expected failures stored in the global state and display a fallback screen */}
                      <UpdateHandler>
                        <RouterProvider router={router} />
                      </UpdateHandler>
                    </Suspense>
                  </EmbeddingPartnerWrapper>
                </OperatingZoneWrapper>
              </SessionLanguageProvider>
            </SessionDefaultLanguageWrapper>
          </SessionProvider>
        </ToastProvider>
      </TemporaryNotificationAlertProvider>
    </PharaohWrapper>
  )
})
